/**
 * Cross-tab mutual exclusion and coalescing for `POST /auth/refresh`.
 *
 * <h3>What this is guarding</h3>
 * Every tab shares one `bmo_session` cookie, therefore one BFF session, therefore one stored
 * ISAM refresh token. ISAM rotates that token on use, so two tabs refreshing at the same
 * moment send two grants for the same token: the loser gets `invalid_grant`, and
 * `AuthEndpoints.HandleRefresh` responds to a failed grant by DELETING the session — logging
 * every tab out at once, and taking with it whatever unsaved answers FormShell was holding in
 * memory. `useSessionTimer`'s `inFlightRef` closes that window inside a single tab and can do
 * nothing about it across tabs.
 *
 * The hazard is latent rather than live today: `HandleIsamLogin` requests
 * `scope=openid profile email`, without `offline_access`, so no refresh token is stored and
 * the grant branch never runs — `/auth/refresh` is currently a keep-alive. It arms itself the
 * moment that scope is added (ADR-0022 open question 1).
 *
 * <h3>Two mechanisms, because one is not enough</h3>
 * The Web Locks API gives real mutual exclusion across tabs of an origin, so refreshes
 * serialize instead of racing. But serializing alone only converts a collision into a queue:
 * the tab that waited would wake up and immediately fire a second, redundant grant against the
 * token the winner just rotated. So the freshness stamp below is checked INSIDE the lock —
 * whoever waited sees the refresh that just happened and does nothing. Five tabs pinging
 * together produce exactly one grant.
 */

/**
 * Unix milliseconds of the last successful refresh, shared across tabs. Not a security value —
 * a tampered stamp only delays a refresh until the next tick.
 */
const REFRESH_STAMP_KEY = 'bmo-inflow.sessionRefreshedAt';

const LOCK_NAME = 'bmo-inflow.session-refresh';

/**
 * A refresh newer than this leaves the BFF's stored access token fresh enough that another
 * grant buys nothing. Comfortably under the 10-minute `AccessTokenTtlMinutes`, so a caller
 * about to spend the token still gets one with most of its life ahead of it, and comfortably
 * over the time it takes a burst of tabs to file through the lock.
 */
export const REFRESH_COALESCE_MS = 30_000;

/**
 * When the BFF's stored ISAM access token expires, in unix SECONDS — the server's wire format,
 * kept as-is so the two never disagree through a conversion.
 *
 * <h3>Why this is shared across tabs rather than held per-tab</h3>
 * It is what the activity ping consults to decide whether a refresh is needed at all. Held in a
 * React ref, only the tab that performed the last refresh would know the current value; every
 * other tab would still hold whatever `/me` reported when it mounted, conclude the token is long
 * dead, and ping on every single save — which is the round-trip this check exists to remove.
 * Since followers are the tabs the advisor is most likely to be working in (leadership belongs
 * to the longest-standing tab, not the focused one), per-tab state would miss the common case.
 *
 * Not a security value, like the refresh stamp above it: a tampered expiry only causes a
 * needless ping, or a save that discovers the truth from the BFF one request later.
 */
const ACCESS_TOKEN_EXPIRY_KEY = 'bmo-inflow.accessTokenExpiresAt';

/**
 * Treat the access token as spent when less than this remains. Must stay comfortably ABOVE the
 * backend's own 60-second skew (`AuthEndpoints.AccessTokenRefreshSkewSeconds`), so this check
 * can never skip a ping the BFF would have answered with a grant — the margin between them
 * costs one no-op round-trip, while the reverse would hand a save a token that dies in flight.
 * The extra 60 seconds on top also absorbs drift between the browser's clock and the server's.
 */
export const ACCESS_TOKEN_FRESH_MARGIN_MS = 120_000;

/**
 * When the BFF's stored ISAM REFRESH token expires, in unix seconds — the deadline that ends
 * the session, as opposed to {@link ACCESS_TOKEN_EXPIRY_KEY}, which is merely the thing a
 * refresh renews.
 *
 * <h3>Why the browser is told this at all</h3>
 * Only a grant rotates the refresh token, and only the timed tick forces a grant, so the gap
 * between the tick (15 min) and this deadline (20 min) is the entire budget a failed refresh
 * has to recover in. Without the deadline on the wire, every client-side safeguard — how long
 * to keep retrying a tick that 503'd, how late a leadership handoff may land — has to be
 * inferred from the poll cadence, which is a proxy for this number rather than this number.
 *
 * Shared across tabs for the same reason the access-token stamp is: the tab that performed the
 * last refresh is rarely the tab the advisor is working in.
 */
const REFRESH_TOKEN_EXPIRY_KEY = 'bmo-inflow.refreshTokenExpiresAt';

export function markAccessTokenExpiresAt(unixSeconds: number): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, String(unixSeconds));
  } catch {
    // Private mode or quota. Reads then return null, which reads as "not fresh" and restores
    // the old ping-on-every-save behaviour — slower, never wrong.
  }
}

/**
 * Record the refresh-token deadline the server just reported. A null or absent value CLEARS the
 * stamp rather than storing a sentinel: the server sends null for a session that has no refresh
 * token (Entra) or whose deadline it could not derive, and an older build sends nothing at all.
 * Leaving the previous session's value parked under any of those would hand a reader a deadline
 * belonging to a credential that no longer exists.
 */
export function markRefreshTokenExpiresAt(unixSeconds: number | null | undefined): void {
  try {
    if (typeof unixSeconds !== 'number' || !Number.isFinite(unixSeconds)) {
      localStorage.removeItem(REFRESH_TOKEN_EXPIRY_KEY);
      return;
    }
    localStorage.setItem(REFRESH_TOKEN_EXPIRY_KEY, String(unixSeconds));
  } catch {
    // Private mode or quota. Reads return null, which every consumer must already treat as
    // "deadline unknown" — see readRefreshTokenExpiresAt.
  }
}

/**
 * The stored refresh-token deadline in unix seconds, or null when it is unknown.
 *
 * <h3>Null is "unknown", never "expired"</h3>
 * It is absent before the first refresh of a tab's session, on an Entra session, on a backend
 * too old to send the field, and whenever storage is unavailable. A consumer that treats those
 * as "the deadline has passed" would end a perfectly live session; one that treats them as
 * "no deadline" would retry forever. Both are decisions for the caller to make explicitly,
 * which is why this returns null rather than picking one.
 */
export function readRefreshTokenExpiresAt(): number | null {
  try {
    const raw = localStorage.getItem(REFRESH_TOKEN_EXPIRY_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearRefreshTokenExpiry(): void {
  try {
    localStorage.removeItem(REFRESH_TOKEN_EXPIRY_KEY);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

/** The stored expiry in unix seconds, or null when absent or unreadable. */
export function readAccessTokenExpiresAt(): number | null {
  try {
    const raw = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearAccessTokenExpiry(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

/**
 * True when the stored ISAM access token has enough life left that a save can spend it without
 * refreshing first.
 *
 * Every uncertain case answers FALSE — absent, unparseable, or a timestamp in the future beyond
 * any plausible token lifetime (a backwards clock step). The cost of a wrong `false` is one
 * redundant request the BFF answers without spending a grant; the cost of a wrong `true` is a
 * save sent with a dead token, which surfaces to the advisor as an unexplained failure.
 */
export function accessTokenFresh(): boolean {
  try {
    const raw = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    if (!raw) return false;
    const expiresAtMs = Number(raw) * 1000;
    if (!Number.isFinite(expiresAtMs)) return false;
    return expiresAtMs - Date.now() > ACCESS_TOKEN_FRESH_MARGIN_MS;
  } catch {
    return false;
  }
}

/**
 * True only when we KNOW the stored access token is spent — a readable stamp, already in the
 * past. Asked after a mandatory refresh to decide whether the save may proceed.
 *
 * <h3>Why this is not simply `!accessTokenFresh()`</h3>
 * They answer different questions and must disagree in the middle. `accessTokenFresh` asks
 * "should I refresh first?" and uses a two-minute margin, deliberately wider than the backend's
 * own sixty-second skew — so a ping at 1m30s to spare is answered with no grant at all, and the
 * token is left with exactly the life it had. Treating that as a failure would block a save the
 * token could have carried perfectly well. This asks the narrower question: is it actually gone?
 *
 * An unreadable or absent stamp answers FALSE, the opposite of `accessTokenFresh`, and for the
 * same reason both are the safe direction: not knowing should cost a redundant refresh, never a
 * blocked save. In private mode, where neither value ever persists, saves refresh every time and
 * are never blocked — exactly the behaviour that predates this check.
 */
export function accessTokenKnownExpired(): boolean {
  try {
    const raw = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    if (!raw) return false;
    const expiresAtMs = Number(raw) * 1000;
    if (!Number.isFinite(expiresAtMs)) return false;
    return expiresAtMs <= Date.now();
  } catch {
    return false;
  }
}

/** Structural type — `LockManager` is absent from older TS DOM libs and from jsdom entirely. */
type LockManagerLike = {
  request: <T>(name: string, callback: () => Promise<T>) => Promise<T>;
};

function lockManager(): LockManagerLike | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  return typeof locks?.request === 'function' ? locks : undefined;
}

export function markSessionRefreshed(at: number = Date.now()): void {
  try {
    localStorage.setItem(REFRESH_STAMP_KEY, String(at));
  } catch {
    // Private mode or quota. Coalescing degrades to per-tab, which is where it started.
  }
}

/** True when a successful refresh landed within `ms` — in THIS tab or any other. */
export function sessionRefreshedWithin(ms: number): boolean {
  try {
    const raw = localStorage.getItem(REFRESH_STAMP_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    // A stamp in the future means the clock stepped backwards; treat it as stale rather than
    // as permanently fresh, which would suppress refreshes until the clock caught up.
    const age = Date.now() - at;
    return age >= 0 && age < ms;
  } catch {
    return false;
  }
}

export function clearSessionRefreshStamp(): void {
  try {
    localStorage.removeItem(REFRESH_STAMP_KEY);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

/**
 * A per-document tail used when Web Locks is unavailable (jsdom, and browsers predating it).
 * It only serializes within this tab, which is strictly weaker — but it is exactly the
 * guarantee the code had before this module existed, so the fallback never regresses anything.
 */
let fallbackTail: Promise<unknown> = Promise.resolve();

/**
 * Run `fn` as the only session refresh in flight across every tab of this origin.
 *
 * `fn` is expected to check {@link sessionRefreshedWithin} itself and no-op when another tab
 * has just refreshed — the lock decides who goes first, the stamp decides who needs to go at
 * all. A rejection propagates to the caller and releases the lock.
 */
export function withSessionRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = lockManager();
  if (locks) return locks.request(LOCK_NAME, fn);

  const run = fallbackTail.then(fn, fn);
  // Chain on settlement, not on success: a rejected refresh must not poison the queue and
  // strand every later caller.
  fallbackTail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}
